/**
 * @fileoverview Optional WebAssembly backend for the ligand evaluator.
 *
 * This is a performance path only. The JS `Evaluator` (forces/evaluator.js) is
 * the reference and the transparent fallback. This backend computes in single
 * precision (f32) inside WASM, so it does not reproduce the f64 JS reference
 * bit-for-bit; it agrees with it only to f32 accuracy (~1e-3 relative). The f32
 * gradient is widened back to f64 on copy-out so the f64 L-BFGS driver and
 * callers see a Float64Array. `WasmEvaluator` exposes the same
 * `evaluate(coords) -> {energy, gradient}` surface as the JS `Evaluator`, so the
 * L-BFGS driver and the validation harness can drive either interchangeably.
 *
 * Loading is async + environment-split (mirrors index.js's lazy worker):
 *   - browser/worker: dynamic `import('./wasmFactory.web.js')`, whose static
 *     `.wasm` import is turned into a URL by the webpack file-loader rule.
 *   - node/mocha: `require('./evaluator.node.js')` (kept off webpack's static
 *     graph via an indirect require) — the glue auto-resolves its `.wasm` via
 *     `__dirname`.
 * If neither is available, the caller falls back to the JS engine.
 *
 * Marshalling: all static per-ligand data (topology, parameters, the pruned
 * receptor candidate set, restraints) is copied into WASM linear memory once
 * at construction and handed to the C context via setters; only `coords` cross
 * the boundary per evaluate() (3N in, energy out, 3N gradient out). Heap views
 * (`HEAPF32`/`HEAP32`) are re-read on every access because `ALLOW_MEMORY_GROWTH`
 * can detach them after a `_malloc`.
 *
 * @see ./evaluator.c        (the C kernels, line-for-line port of forces/*)
 * @see ./wasmFactory.web.js (browser glue + file-loader .wasm URL)
 * @see ../forces/evaluator.js (the reference JS Evaluator)
 */

/**
 * @typedef {import('../types.js').LigandInput} LigandInput
 * @typedef {import('../types.js').MinimizationOptions} MinimizationOptions
 * @typedef {import('../forces/receptor.js').ReceptorContext} ReceptorContext
 */

/** Nonbonded cutoff (nm); mirrors evaluator.js NO_CUTOFF (mm-server NoCutoff). */
const NO_CUTOFF = 1e9;

/**
 * Cached ready-module promise (one Emscripten instance per process/tab is
 * plenty — it is stateless across WasmEvaluator instances, which each own
 * their own C context + heap buffers).
 * @type {Promise<object>|null}
 */
let _modulePromise = null;

/** Test seam: when true, getWasmModule rejects, forcing the JS fallback. */
let _forceUnavailableForTests = false;

/** One-time guard so the WASM->JS fallback warning is emitted at most once. */
let _warnedFallback = false;

/**
 * True in node-like environments (mocha/SSR), false in a browser/worker.
 * @returns {boolean}
 */
function isNodeEnv() {
    return typeof process !== 'undefined'
        && process.versions != null
        && process.versions.node != null
        && typeof window === 'undefined'
        && typeof WorkerGlobalScope === 'undefined';
}

/**
 * Lazily load + instantiate the Emscripten module for this environment.
 * Resolves to the ready Module, or rejects if the backend cannot load (the
 * caller treats a rejection as "WASM unavailable" and uses the JS engine).
 *
 * @returns {Promise<object>}
 */
export function getWasmModule() {
    if (_forceUnavailableForTests) {
        return Promise.reject(new Error('WASM forced unavailable (test)'));
    }
    if (_modulePromise) return _modulePromise;
    const load = isNodeEnv()
        ? Promise.resolve().then(() => {
            // Indirect require keeps the node glue off webpack's static graph
            // (it references node:fs, which a browser bundle must never pull in).
            // eslint-disable-next-line no-eval
            const req = eval('require');
            const createMinimizerModule = req('./evaluator.node.js');
            return createMinimizerModule();
        })
        : import('./wasmFactory.web.js').then((m) => m.loadWasmModule());
    // Don't cache a rejection: a transient first-load failure shouldn't pin the
    // process to JS forever — clear the cache so a later call can retry. (The
    // caller still falls back to JS for this attempt.)
    _modulePromise = load.catch((err) => {
        _modulePromise = null;
        throw err;
    });
    return _modulePromise;
}

/** Reset cached module + test/fallback flags (tests only). @returns {void} */
export function _resetWasmModuleForTests() {
    // Drop the cached evaluator first: it holds buffers in the module we're
    // about to discard, so reusing it after a module swap would be invalid.
    _resetWasmEvaluatorCache();
    _modulePromise = null;
    _forceUnavailableForTests = false;
    _warnedFallback = false;
}

/** Force the JS fallback (tests only): make the WASM module fail to load. */
export function _forceWasmUnavailableForTests(on = true) {
    // Same rationale as _resetWasmModuleForTests: don't keep an evaluator that
    // points at a module the next call must not use.
    _resetWasmEvaluatorCache();
    _forceUnavailableForTests = on;
    _modulePromise = null;
}

/**
 * Copy a numeric array into a freshly-malloc'd f32 region. Returns the byte
 * pointer (0 for empty — the C side never dereferences a zero-count array).
 * `TypedArray.set` auto-converts the incoming f64 JS arrays down to f32.
 * @param {object} mod
 * @param {ArrayLike<number>} arr
 * @returns {number}
 */
function allocF32(mod, arr) {
    if (!arr || arr.length === 0) return 0;
    const ptr = mod._malloc(arr.length * 4);
    // _malloc returns 0 on OOM. Writing at offset 0 would silently corrupt the
    // module's heap base; fail loudly instead so an OOM is a clear error.
    if (ptr === 0) throw new Error(`wasm allocF32: _malloc(${arr.length * 4}) returned 0 (out of memory)`);
    mod.HEAPF32.set(arr, ptr >>> 2); // re-read HEAPF32: malloc may have grown it
    return ptr;
}

/**
 * Copy a numeric array into a freshly-malloc'd i32 region.
 * @param {object} mod
 * @param {ArrayLike<number>} arr
 * @returns {number}
 */
function allocI32(mod, arr) {
    if (!arr || arr.length === 0) return 0;
    const ptr = mod._malloc(arr.length * 4);
    // _malloc returns 0 on OOM. Writing at offset 0 would silently corrupt the
    // module's heap base; fail loudly instead so an OOM is a clear error.
    if (ptr === 0) throw new Error(`wasm allocI32: _malloc(${arr.length * 4}) returned 0 (out of memory)`);
    mod.HEAP32.set(arr, ptr >>> 2);
    return ptr;
}

/**
 * WASM-backed evaluator with the same surface as the JS `Evaluator`.
 *
 * Construct via {@link createWasmEvaluator} (async — it awaits the module).
 * The constructor lays all static data into linear memory and configures the C
 * context; {@link WasmEvaluator#evaluate} then runs with only a coords copy-in
 * and a gradient copy-out per call.
 *
 * The C kernels compute in single precision (f32): inputs are down-converted to
 * f32 at marshal time, and the returned gradient is widened back to f64 for the
 * f64 L-BFGS driver and callers (see {@link WasmEvaluator#evaluate}).
 */
export class WasmEvaluator {
    /**
     * @param {object} mod                     Ready Emscripten Module.
     * @param {LigandInput} ligand             Topology + parameters (+ coords; coords unused here).
     * @param {ReceptorContext|null} receptorContext  Prepared receptor or null.
     * @param {MinimizationOptions} [options]  Restraint knobs (iteration knobs ignored).
     */
    constructor(mod, ligand, receptorContext = null, options = {}) {
        const { topology, parameters } = ligand;
        const nAtoms = topology.nAtoms;

        this.mod = mod;
        this.nAtoms = nAtoms;
        /** Byte pointers we own and must free in dispose(). @type {number[]} */
        this._ptrs = [];

        const keep = (ptr) => { if (ptr) this._ptrs.push(ptr); return ptr; };

        const ctx = mod._em_createContext(nAtoms, NO_CUTOFF);
        this.ctx = ctx;

        // --- bonded ---
        const nBonds = parameters.bondK.length;
        mod._em_setBonds(
            ctx,
            keep(allocI32(mod, topology.bonds)), nBonds,
            keep(allocF32(mod, parameters.bondK)),
            keep(allocF32(mod, parameters.bondR0)),
        );
        const nAngles = parameters.angleK.length;
        mod._em_setAngles(
            ctx,
            keep(allocI32(mod, topology.angles)), nAngles,
            keep(allocF32(mod, parameters.angleK)),
            keep(allocF32(mod, parameters.angleTheta0)),
        );
        const nTorsions = parameters.torsionTermsPerTorsion.length;
        mod._em_setTorsions(
            ctx,
            keep(allocI32(mod, topology.torsions)), nTorsions,
            keep(allocI32(mod, parameters.torsionTermsPerTorsion)),
            keep(allocF32(mod, parameters.torsionK)),
            keep(allocI32(mod, parameters.torsionN)),
            keep(allocF32(mod, parameters.torsionPhase)),
        );

        // --- ligand nonbonded ---
        mod._em_setNonbonded(
            ctx,
            keep(allocF32(mod, parameters.charges)),
            keep(allocF32(mod, parameters.sigma)),
            keep(allocF32(mod, parameters.epsilon)),
            keep(allocI32(mod, parameters.exclusions)), parameters.exclusions.length,
            keep(allocI32(mod, parameters.scaledPairs)), parameters.scaledPairs.length,
        );

        // --- receptor (ligand <-> static receptor) ---
        if (receptorContext && receptorContext.nCandidates > 0) {
            const nRec = receptorContext.nCandidates;
            mod._em_setReceptor(
                ctx, nRec,
                keep(allocF32(mod, receptorContext.coords)),
                keep(allocF32(mod, receptorContext.charges)),
                keep(allocF32(mod, receptorContext.sigma)),
                keep(allocF32(mod, receptorContext.epsilon)),
            );
        }

        // --- positional restraints (decode like evaluator.js) ---
        const restraints = options.positionalRestraints || [];
        const nR = restraints.length;
        if (nR > 0) {
            const atoms = new Int32Array(nR);
            const ks = new Float64Array(nR);
            const r0 = new Float64Array(3 * nR);
            for (let i = 0; i < nR; i++) {
                atoms[i] = restraints[i].atom;
                ks[i] = restraints[i].k;
                r0[3 * i] = restraints[i].r0[0];
                r0[3 * i + 1] = restraints[i].r0[1];
                r0[3 * i + 2] = restraints[i].r0[2];
            }
            mod._em_setRestraints(
                ctx, nR,
                keep(allocI32(mod, atoms)),
                keep(allocF32(mod, ks)),
                keep(allocF32(mod, r0)),
            );
        }

        // --- per-eval coords/gradient buffers (reused across calls) ---
        const coordsPtr = mod._malloc(3 * nAtoms * 4);
        const gradPtr = mod._malloc(3 * nAtoms * 4);
        // _malloc returns 0 on OOM; a 0 here would corrupt the module on every
        // evaluate (writes/reads at heap offset 0). Fail loudly.
        if (coordsPtr === 0 || gradPtr === 0) {
            throw new Error(`wasm evaluator: _malloc for coords/grad buffers (${3 * nAtoms * 4} bytes) returned 0 (out of memory)`);
        }
        this.coordsPtr = keep(coordsPtr);
        this.gradPtr = keep(gradPtr);
        /**
         * Reused gradient copy returned to callers. WASM computes the gradient
         * in f32 internally; on copy-out those f32 values are widened back into
         * this Float64Array for the f64 L-BFGS driver and callers.
         * @type {Float64Array}
         */
        this.gradient = new Float64Array(3 * nAtoms);
    }

    /**
     * Evaluate energy + gradient at `coords`. Returns the energy (kJ/mol) and a
     * gradient (kJ/mol/nm) in `this.gradient` (reused — copy if retaining),
     * matching the JS Evaluator contract.
     *
     * @param {Float64Array} coords  Flat length 3*N (nm).
     * @returns {{energy: number, gradient: Float64Array}}
     */
    evaluate(coords) {
        const mod = this.mod;
        const n = 3 * this.nAtoms;
        // Copy coords in (re-read HEAPF32 each call: growth may have detached
        // it). `set` auto-converts the f64 coords down to f32.
        mod.HEAPF32.set(coords, this.coordsPtr >>> 2);
        const energy = mod._em_evaluate(this.ctx, this.coordsPtr, this.gradPtr);
        // Bulk copy the gradient out (re-read HEAPF32: growth may have detached
        // it). The f32 values widen back into the Float64Array on copy-out.
        const base = this.gradPtr >>> 2;
        this.gradient.set(mod.HEAPF32.subarray(base, base + n));
        return { energy, gradient: this.gradient };
    }

    /** Free all owned linear-memory buffers + the C context. @returns {void} */
    dispose() {
        if (!this.ctx) return;
        this.mod._em_destroyContext(this.ctx);
        for (const ptr of this._ptrs) this.mod._free(ptr);
        this._ptrs = [];
        this.ctx = 0;
    }
}

/**
 * Async factory: await the module, then build a {@link WasmEvaluator}.
 *
 * @param {LigandInput} ligand
 * @param {ReceptorContext|null} [receptorContext]
 * @param {MinimizationOptions} [options]
 * @returns {Promise<WasmEvaluator>}
 */
export async function createWasmEvaluator(ligand, receptorContext = null, options = {}) {
    const mod = await getWasmModule();
    return new WasmEvaluator(mod, ligand, receptorContext, options);
}

/* ------------------------------------------------------------------------- *
 * WasmEvaluator instance cache.
 *
 * Building a WasmEvaluator marshals all static data (topology + parameters +
 * pruned receptor + restraints) into linear memory — many `_malloc`s, a
 * per-buffer HEAP copy, the C-side sqrt(eps) prepass, and the receptor SoA
 * deinterleave. The per-call `evaluate(coords)` only crosses 3N coords in and
 * energy + 3N gradient out, so a minimize() loop (hundreds of evaluates) that
 * rebuilds the evaluator each *call* re-pays all of that marshalling needlessly.
 *
 * This module-scoped single-slot cache keeps one built evaluator alive and
 * reuses it across `minimizeWithOptionalWasm` calls when the static inputs are
 * byte-for-byte identical to what's already laid into the C context.
 *
 * Correctness is paramount. A stale reuse would silently corrupt energies, so
 * the cache key is not a hash (a collision would be a silent miscompute): it is
 * an exact element-wise comparison of every array that gets marshalled, plus
 * the scalar discriminators (nAtoms, NO_CUTOFF, forcefieldVersion, and every
 * array length). Reference identity is taken as a definitive match (same object
 * => same bytes) for the main-thread path where the ligand/receptor objects are
 * stable; the worker path structure-clones inputs each message, so equality
 * falls through to the exact element compare. On any mismatch — or any
 * structural surprise — we miss and rebuild, after disposing the stale instance
 * (no leak). A false miss is merely a rebuild (slow), never wrong.
 * ------------------------------------------------------------------------- */

/**
 * The single cached entry, or null. Holds the live evaluator plus the exact
 * static inputs it was built from (so the next call can verify identity).
 * @type {{ evaluator: WasmEvaluator, key: object }|null}
 */
let _evaluatorCache = null;

/** True iff `a` and `b` are equal length and equal element-by-element. */
function arraysEqual(a, b) {
    if (a === b) return true;
    if (!a || !b) return !a && !b; // both null/empty-ish only
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}

/**
 * Normalize the positional restraints into the same decoded arrays the
 * constructor marshals, so identity is checked against EXACTLY what is laid
 * into the C context (not the original restraint objects).
 * @param {MinimizationOptions} options
 * @returns {{ nR: number, atoms: Int32Array, ks: Float64Array, r0: Float64Array }}
 */
function decodeRestraints(options) {
    const restraints = (options && options.positionalRestraints) || [];
    const nR = restraints.length;
    const atoms = new Int32Array(nR);
    const ks = new Float64Array(nR);
    const r0 = new Float64Array(3 * nR);
    for (let i = 0; i < nR; i++) {
        atoms[i] = restraints[i].atom;
        ks[i] = restraints[i].k;
        r0[3 * i] = restraints[i].r0[0];
        r0[3 * i + 1] = restraints[i].r0[1];
        r0[3 * i + 2] = restraints[i].r0[2];
    }
    return {
        nR, atoms, ks, r0,
    };
}

/**
 * Capture every static input that gets marshalled into the C context, so two
 * calls can be compared for byte-exact identity. Coords are deliberately
 * excluded (they cross per evaluate and never enter the static set).
 * @param {LigandInput} ligand
 * @param {ReceptorContext|null} receptorContext
 * @param {MinimizationOptions} options
 * @returns {object}
 */
function makeCacheKey(ligand, receptorContext, options) {
    const { topology, parameters } = ligand;
    const rec = (receptorContext && receptorContext.nCandidates > 0) ? receptorContext : null;
    return {
        nAtoms: topology.nAtoms,
        ffVersion: parameters.forcefieldVersion,
        topology,
        parameters,
        // Receptor identity: keep the exact arrays we marshal (or null).
        rec: rec ? {
            n: rec.nCandidates,
            coords: rec.coords,
            charges: rec.charges,
            sigma: rec.sigma,
            epsilon: rec.epsilon,
        } : null,
        restraints: decodeRestraints(options),
    };
}

/**
 * Exact identity test between two cache keys: same scalars, same length, and
 * same elements across every marshalled array. Any difference => not identical
 * (=> rebuild). This is what guarantees a reused evaluator's static data is the
 * data the caller asked for.
 * @param {object} a
 * @param {object} b
 * @returns {boolean}
 */
function cacheKeyMatches(a, b) {
    if (!a || !b) return false;
    if (a.nAtoms !== b.nAtoms) return false;
    if (a.ffVersion !== b.ffVersion) return false;

    const ta = a.topology; const tb = b.topology;
    if (!arraysEqual(ta.bonds, tb.bonds)) return false;
    if (!arraysEqual(ta.angles, tb.angles)) return false;
    if (!arraysEqual(ta.torsions, tb.torsions)) return false;

    const pa = a.parameters; const pb = b.parameters;
    if (!arraysEqual(pa.charges, pb.charges)) return false;
    if (!arraysEqual(pa.sigma, pb.sigma)) return false;
    if (!arraysEqual(pa.epsilon, pb.epsilon)) return false;
    if (!arraysEqual(pa.bondK, pb.bondK)) return false;
    if (!arraysEqual(pa.bondR0, pb.bondR0)) return false;
    if (!arraysEqual(pa.angleK, pb.angleK)) return false;
    if (!arraysEqual(pa.angleTheta0, pb.angleTheta0)) return false;
    if (!arraysEqual(pa.torsionTermsPerTorsion, pb.torsionTermsPerTorsion)) return false;
    if (!arraysEqual(pa.torsionK, pb.torsionK)) return false;
    if (!arraysEqual(pa.torsionN, pb.torsionN)) return false;
    if (!arraysEqual(pa.torsionPhase, pb.torsionPhase)) return false;
    if (!arraysEqual(pa.exclusions, pb.exclusions)) return false;
    if (!arraysEqual(pa.scaledPairs, pb.scaledPairs)) return false;

    // Receptor: both vacuum, or both present with identical candidate arrays.
    if (!a.rec || !b.rec) {
        if (a.rec || b.rec) return false;
    } else {
        if (a.rec.n !== b.rec.n) return false;
        if (!arraysEqual(a.rec.coords, b.rec.coords)) return false;
        if (!arraysEqual(a.rec.charges, b.rec.charges)) return false;
        if (!arraysEqual(a.rec.sigma, b.rec.sigma)) return false;
        if (!arraysEqual(a.rec.epsilon, b.rec.epsilon)) return false;
    }

    // Restraints (the decoded arrays, matching what the constructor marshals).
    const ra = a.restraints; const rb = b.restraints;
    if (ra.nR !== rb.nR) return false;
    if (!arraysEqual(ra.atoms, rb.atoms)) return false;
    if (!arraysEqual(ra.ks, rb.ks)) return false;
    if (!arraysEqual(ra.r0, rb.r0)) return false;

    return true;
}

/**
 * Return a {@link WasmEvaluator} for these static inputs, reusing the cached
 * instance when its static data is byte-identical, else disposing it and
 * building a fresh one. The returned evaluator is owned by the cache — callers
 * must not dispose it (it is reused on the next call and freed only when the
 * cache is invalidated or replaced).
 *
 * @param {LigandInput} ligand
 * @param {ReceptorContext|null} receptorContext
 * @param {MinimizationOptions} options
 * @returns {Promise<WasmEvaluator>}
 */
async function getCachedWasmEvaluator(ligand, receptorContext, options) {
    const key = makeCacheKey(ligand, receptorContext, options);
    if (_evaluatorCache && cacheKeyMatches(_evaluatorCache.key, key)) {
        return _evaluatorCache.evaluator; // hit: static data already laid in
    }
    // Miss (changed ligand/receptor/restraints, or first call): drop the stale
    // instance (frees its linear-memory buffers + C context) BEFORE building.
    if (_evaluatorCache) {
        _evaluatorCache.evaluator.dispose();
        _evaluatorCache = null;
    }
    const evaluator = await createWasmEvaluator(ligand, receptorContext, options);
    _evaluatorCache = { evaluator, key };
    return evaluator;
}

/**
 * Drop and dispose the cached evaluator (frees its WASM buffers). Used by tests
 * to guarantee a clean slate; never needed in a real worker (module state dies
 * with the worker) but harmless to call.
 * @returns {void}
 */
export function _resetWasmEvaluatorCache() {
    if (_evaluatorCache) {
        _evaluatorCache.evaluator.dispose();
        _evaluatorCache = null;
    }
}

/**
 * Emit a one-time warning when the WASM backend is requested but degrades to the
 * JS engine. Without this, a WASM-unavailable run is invisible (looks merely
 * "slow"), so it is worth a single console line the first time it happens.
 * @param {Error} err
 * @returns {void}
 */
function warnWasmFallbackOnce(err) {
    if (_warnedFallback) return;
    _warnedFallback = true;
    const detail = (err && err.message) || String(err);
    // eslint-disable-next-line no-console
    if (typeof console !== 'undefined' && console.warn) {
        console.warn(`[minimizer] WASM backend unavailable; using the JS engine. (${detail})`);
    }
}

/**
 * Run the minimizer driver through the WASM evaluator when `useWasm`, falling
 * back transparently to the JS engine if the WASM module cannot load (or
 * `useWasm` is false). Shared by both the main-thread (index.js) and worker
 * (worker.js) paths so the two have one fallback implementation.
 *
 * The driver is injected (rather than imported) to keep this backend module
 * free of a dependency on the minimizer driver. The WASM evaluator is always
 * disposed (it owns linear-memory buffers), even if the driver throws.
 *
 * @param {Function} runMinimize  (ligand, receptorContext, options) -> MinimizationResult
 * @param {LigandInput} ligand
 * @param {ReceptorContext|null} receptorContext
 * @param {MinimizationOptions} options
 * @param {boolean} useWasm
 * @returns {Promise<object>}  The MinimizationResult (activeBackend 'wasm' or 'cpu').
 */
export async function minimizeWithOptionalWasm(
    runMinimize, ligand, receptorContext, options, useWasm,
) {
    if (useWasm) {
        let evaluator = null;
        try {
            // Reuse a cached evaluator when the static data (topology + receptor
            // + restraints) is byte-identical to the last call, so only `coords`
            // cross the boundary per evaluate. The cache owns the returned
            // instance's lifetime — do not dispose it here.
            evaluator = await getCachedWasmEvaluator(ligand, receptorContext, options);
        } catch (err) {
            // Module could not load (or a build/OOM error) -> transparent JS
            // fallback (warn once). The cache was not populated on this path.
            warnWasmFallbackOnce(err);
        }
        if (evaluator) {
            return runMinimize(ligand, receptorContext, {
                ...options, evaluator, activeBackend: 'wasm',
            });
        }
    }
    return runMinimize(ligand, receptorContext, options);
}
